import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const SITE_URL = "https://structuramarkets.com";
const OG_IMAGE = `${SITE_URL}/og-image.png`;
const OG_IMAGE_ALT = "Structura - Market structure intelligence";
const TWITTER_SITE = "@StructuraMkts";

interface SEOProps {
  title: string;
  description: string;
  path: string;
  ogType?: string;
  ogImage?: string;
  ogImageAlt?: string;
  noindex?: boolean;
}

export function SEO({ title, description, path, ogType = "website", ogImage = OG_IMAGE, ogImageAlt = OG_IMAGE_ALT, noindex = false }: SEOProps) {
  const canonical = `${SITE_URL}${path}`;

  useEffect(() => {
    const selectors = [
      'meta[name="description"]',
      'link[rel="canonical"]',
      'meta[property="og:type"]',
      'meta[property="og:url"]',
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[property="og:image"]',
      'meta[property="og:image:alt"]',
      'meta[name="twitter:url"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
      'meta[name="twitter:image"]',
      'meta[name="twitter:image:alt"]',
    ];

    selectors.forEach(selector => {
      const elements = Array.from(document.head.querySelectorAll(selector));
      elements.slice(0, -1).forEach(element => element.remove());
    });
  }, [title, description, canonical, ogType, ogImage, ogImageAlt]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Structura" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_SITE} />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
    </Helmet>
  );
}
